import { CancelToken } from 'axios'
import { BaseApiParams as BP, CRUDInterface, SortablePositions } from '@/typings/common'


export interface MapParams extends BP {
  id?: number;
  cancelToken?: CancelToken;
}

export interface IdTransformer {
  id?: number;
  params: BP;
  options: MapParams;
}

export const idTransformer = (params: BP, rawParams?: MapParams): IdTransformer => {
  const { id, cancelToken } = rawParams || {}
  const options = { cancelToken }
  return { id, params, options }
}

export class TransformerParams<T, TT = T> implements CRUDInterface {
  protected defaultParams: string[] = [];

  protected delete_multiple_param = 'ids';

  protected per_page = 'pageSize';

  protected getParamsWithDefaults(params: MapParams = {}) {
    return this.defaultParams.reduce((acc, key) => {
      const value = params[key]
      if (value !== void 0) acc[key] = value
      return acc
    }, {} as Record<string, unknown>)
  }

  public getAll(
    payload: MapParams = {},
    rawParams: MapParams = {},
  ): { params: MapParams; options: MapParams } {
    const { per_page, ...args } = payload || {}
    const { cancelToken } = rawParams

    const params = {
      ...args,
      [this.per_page]: per_page,
      ...this.getParamsWithDefaults(rawParams),
    }
    const options = { cancelToken }
    return { params, options }
  }

  public getOne(payload: BP, rawParams?: MapParams) {
    const params = {
      ...payload,
      ...this.getParamsWithDefaults(rawParams),
    }
    return idTransformer(params, params)
  }

  public create(payload: BP, rawParams?: MapParams) {
    const params = {
      ...payload,
      ...this.getParamsWithDefaults(rawParams),
    }

    const options = {}
    return { params, options }
  }

  public update(payload: MapParams, rawParams: MapParams) {
    const params = {
      ...payload,
      ...this.getParamsWithDefaults(rawParams),
    }

    return idTransformer(params, rawParams)
  }

  public delete(rawParams: MapParams) {
    const params = {
      ...this.getParamsWithDefaults(rawParams),
    }

    return idTransformer(params, rawParams)
  }

  public deleteMultiple(payload: TT[], rawParams?: MapParams): IdTransformer {
    const options = {
      cancelToken: rawParams?.cancelToken,
    }

    const params = {
      ids: payload,
      // @ts-ignore TODO:
      [this.delete_multiple_param]: payload.map((_) => _.id),
      ...this.getParamsWithDefaults(rawParams),
    }

    return { params, options }
  }

  public updatePositions(payload: SortablePositions, rawParams?: Record<string, unknown> & MapParams) {
    const options = {
      cancelToken: rawParams?.cancelToken,
    }

    const params = {
      ...this.getParamsWithDefaults(rawParams),
    }

    const data = payload
    return { data, options, params }
  }
}
